import React, { useState } from "react";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import "../../../../../sass/components/my-documents-page.scss";

import { DocumentsTabs } from "./DocumentsTabs";
import { usePrimaryOrganizationId } from "../../../hooks";
import { useDpas } from "../../../hooks/useDataProtectionAssessment";
import {
  DataProtectionAssessmentDto,
  dpaTypeToHumanReadable,
} from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { Button } from "@mui/material";
import { RouteHelpers, Routes } from "../../../root/routes";
import { iconForDpaType } from "./DPAPageCurrent";
import { Api } from "../../../../common/service/Api";
import { useActionRequest } from "../../../../common/hooks/api";
import { ConfirmDialog } from "../../../components/dialog";
import { Breadcrumbs } from "@truevault/aquilo";

const DPAInfoBox = ({
  orgId,
  dpa,
  refreshDpas,
}: {
  orgId: string;
  dpa: DataProtectionAssessmentDto;
  refreshDpas: () => void;
}) => {
  const { fetch: cancelSignatureRequestAction } = useActionRequest({
    api: async () => {
      await Api.dataProtectionAssessment.cancelSignatureRequest(orgId, dpa.id);
      await refreshDpas();
    },
  });

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  return (
    <div className="dpa-info-box">
      <div className="dpa-info-box__wrapper">
        <div className="dpa-info-box__dpa-type">
          <img src={`/assets/images/stay_compliant/dpa/${iconForDpaType(dpa.type)}`} alt="DPA" />
          <div className="text-weight-bold text-t4 ml-xs">{dpaTypeToHumanReadable(dpa.type)}</div>
        </div>
        <div className="d-flex flex--align-center">
          <Button
            variant="contained"
            color="primary"
            href={RouteHelpers.documents.dataProtectionAssessment.gatherSignatures(dpa.id)}
          >
            View DPA
          </Button>
          <div>
            <a
              className="ml-sm color-red-600"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsCancelDialogOpen(true);
              }}
            >
              Cancel Request
            </a>
            <ConfirmDialog
              open={isCancelDialogOpen}
              onClose={() => setIsCancelDialogOpen(false)}
              confirmText="Cancel Request"
              confirmTitle="This will cancel the signature request"
              contentText={`The approving parties will get notified that the request to sign has been canceled and you will be able to revise your assessment.`}
              onConfirm={async () => {
                await cancelSignatureRequestAction();
                setIsCancelDialogOpen(false);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const DPAPagePending = () => {
  const orgId = usePrimaryOrganizationId();
  const [dpas, dpasRequestState, refreshDpas] = useDpas(orgId);

  const pendingDpas = (dpas || []).filter((dpa) => dpa.status == "REQUESTED_SIGNATURES");

  return (
    <StandardPageWrapper requests={[dpasRequestState]}>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Data Protection Assessments"
          source="Privacy Docs"
          sourceRoute={Routes.documents.root}
        />
      </div>
      <p className="sub-header">Your Assessments</p>
      <DocumentsTabs current="pending" />
      {pendingDpas.length == 0 ? (
        <div className="empty-page">
          <img src="/assets/images/stay_compliant/empty-page.svg" alt="add" />
          <p>No Pending Assessments</p>
        </div>
      ) : (
        <div className="dpa-info-box-grid">
          {pendingDpas.map((dpa) => (
            <DPAInfoBox key={dpa.id} orgId={orgId} dpa={dpa} refreshDpas={refreshDpas} />
          ))}
        </div>
      )}
    </StandardPageWrapper>
  );
};
